import React, {useCallback, useState} from "react";
import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import Button from "../../../components/button/button.jsx";
import PeriodInput from "../../../components/input/period-input.jsx";
import SelectComponent from "../../../components/select/select-component.jsx";
import SentryRow from "../../../components/template/sentry-row.jsx";
import ShowHideColumnsGrid from "../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";
import {AgGridReact} from "ag-grid-react";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import aggridUtils from "../../../utils/aggrid-utils.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {navigateHook} from "../../../hooks/use-navigate.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import projectUtils from "../../../utils/project-utils.js";
import ActivityService from "../../../services/dispatch/activity-service.js";
import UserService from "../../../services/web/user-service.js";
import useHasPermission from "../../../hooks/use-has-permission.js";
import {PermissionEnum} from "../../../utils/enums/permission-enum.js";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const initialFilters = {dtStart: "", dtEnd: "", inspector: null};

const parseBrDate = (value) => {
    if (!value) return null;
    const [dd, mm, yyyy] = value.split("/");
    if (!dd || !mm || !yyyy) return null;
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd));
};

const buildFilter = (values) => [
    {field: "onduty", type: "keywords", value: {dtStart: values.dtStart || "", dtEnd: values.dtEnd || ""}},
    {field: "inspector", type: "=", value: values.inspector?.value ?? ""},
];

const HIDDEN_COLUMNS_STORAGE = "activity-hidden-columns";
const DEFAULT_HIDDEN_COLUMNS = ["id"];

const baseColumns = [
    {
        field: "id",
        headerName: "ID",
        width: 50,
        comparator: (first, second) => String(first ?? "").localeCompare(String(second ?? ""), "pt-BR", {numeric: true}),
    },
    {field: "onduty", headerName: "Plantão", width: 90, comparator: aggridUtils.agGridCompareDate},
    {field: "inspector", headerName: "Cadastrado por", flex: 1},
];

const buildColumnDefs = () => {
    const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? DEFAULT_HIDDEN_COLUMNS;
    return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
};

const ActivityList = () => {
    const hasPermission = useHasPermission();
    const navigate = navigateHook();
    const [filterValues, setFilterValues] = useState(initialFilters);
    const [filter, setFilter] = useState([]);

    const {data = [], isLoading} = useQuery({
        queryFn: () => ActivityService.list({filter: [...filter]}),
        queryKey: ["dispatch-activity", {filter}],
        placeholderData: keepPreviousData,
    });

    const {data: inspectorOptions = []} = useQuery({
        queryFn: () => UserService.getAllNameWarAndId(),
        queryKey: ["user-name-war"],
    });

    const [columnDefs, setColumnDefs] = useState(buildColumnDefs);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const apply = useCallback(() => {
        const {dtStart, dtEnd} = filterValues;

        if ((dtStart && !dtEnd) || (!dtStart && dtEnd)) {
            projectUtils.swalErrorMessage("", "Data inicial e data final devem ser preenchidas.", "Ok");
            return;
        }

        if (dtStart && dtEnd) {
            const start = parseBrDate(dtStart);
            const end = parseBrDate(dtEnd);
            if (start && end && start > end) {
                projectUtils.swalErrorMessage("", "Data inicial não pode ser maior que a final.", "Ok");
                return;
            }
        }

        setFilter(buildFilter(filterValues));
    }, [filterValues]);

    const clear = useCallback(() => {
        setFilterValues(initialFilters);
        setFilter([]);
    }, []);

    const onRowClicked = ({data: row}) => {
        if (!row?.id) return;
        navigate(`${API_DESPACHO_WEB}/activity/form/${row.id}`, true);
    };

    const openForm = () => navigate(API_DESPACHO_WEB + "/activity/form", false);

    useGlobalKey("Enter", apply);
    useGlobalKey("Escape", clear);

    const allowActivityOnlySubordinate = hasPermission(PermissionEnum.DISPATCH_ACTIVITY)

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Diário de plantão</Template.Title>
                <Button onClick={openForm}><FaPlus/>Adicionar diário de plantão</Button>
            </Template.Header>
            <Template.Body className="flex flex-col space-y-2">
                <Card>
                    <Card.Body>
                        <SentryRow className="items-start">
                            <PeriodInput
                                startDateId="dtStart"
                                endDateId="dtEnd"
                                label="Plantão entre"
                                dateFormat="L"
                                returnDateObj={false}
                                startDate={filterValues.dtStart}
                                endDate={filterValues.dtEnd}
                                setFilter={setFilterValues}
                                cols={6}
                            />

                            <div className={"col-span-6 flex justify-between gap-4"}>
                                {
                                    allowActivityOnlySubordinate &&
                                    <SelectComponent
                                        classNameWrapper="w-full" label="Cadastrado por"
                                        options={inspectorOptions}
                                        value={filterValues.inspector}
                                        onChange={(option) => setFilterValues(prev => ({...prev, inspector: option}))}
                                    />
                                }
                                <div className="flex gap-2 justify-end">
                                    <Button onClick={clear} title="Limpar" target="primary-outline"><FaEraser/></Button>
                                    <Button onClick={apply} title="Buscar"><FaSearch/></Button>
                                </div>
                            </div>
                        </SentryRow>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body>
                        <div className="h-[calc(100vh-153px)] aggrid-row-pointer">
                            <AgGridReact
                                rowData={data}
                                columnDefs={columnDefs}
                                defaultColDef={DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL}
                                pagination
                                onRowClicked={onRowClicked}
                                onColumnVisible={onColumnVisible}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                            />
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={columnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                        />
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    );
};

export default ActivityList;
